import { Link } from 'react-router-dom';
import { useSEOHead } from '../../hooks/useSEOHead';

export default function About2BaldGuysPage() {
  useSEOHead({
    title: 'About the 2 Bald Guys | No Hair. No BS. | Rockhound Auto',
    description: 'Meet Dave and Mike, the 2 Bald Guys behind Rockhound Auto\'s brutally honest car buying blog. Former F&I manager and automotive journalist exposing dealer tactics.',
    keywords: 'about 2 bald guys, rockhound auto blog, car buying experts, dealer tactics exposed, honest car advice',
    canonicalUrl: 'https://rockhoundauto.com/about-2-bald-guys',
  });

  return (
    <>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 text-white py-20">
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/479f4ac5ad4914f8ebb9f149988ac52b.jpg')] opacity-10 bg-cover bg-center"></div>
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full mb-6">
              <span className="text-4xl">🧑‍🦲</span>
              <span className="text-lg font-semibold">Meet the Team</span>
              <span className="text-4xl">🧑‍🦲</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              The 2 Bald Guys
            </h1>
            <p className="text-2xl md:text-3xl font-light mb-4 text-emerald-100">
              No Hair. No BS. Just the Truth About Cars.
            </p>
            <p className="text-lg text-emerald-200 max-w-3xl mx-auto">
              Two industry veterans who've seen it all, done it all, and now tell it all — so you don't get taken for a ride.
            </p>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-400">45+</div>
              <div className="text-sm text-emerald-200 mt-1">Combined Years in Auto</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-400">0</div>
              <div className="text-sm text-emerald-200 mt-1">Dealer Sponsors</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-400">∞</div>
              <div className="text-sm text-emerald-200 mt-1">Dealer Tactics Exposed</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-400">100%</div>
              <div className="text-sm text-emerald-200 mt-1">Buyer-Side Loyalty</div>
            </div>
          </div>
        </div>
      </section>

      {/* The Guys */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Dave */}
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-8 border border-slate-200">
              <div className="flex items-start gap-6 mb-6">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center text-4xl flex-shrink-0">
                  🧑‍🦲
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-slate-900 mb-2">Dave</h2>
                  <p className="text-xl text-emerald-700 font-semibold mb-3">Chief Skeptic</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">BS Detector</span>
                    <span className="px-3 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full">F&I Insider</span>
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">Numbers Guy</span>
                  </div>
                </div>
              </div>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  <strong className="text-slate-900">Former F&I Manager turned buyer advocate.</strong> Dave spent 12 years in dealership finance offices, where he learned every trick, tactic, and psychological manipulation dealers use to maximize profit.
                </p>
                <p>
                  After watching too many good people get steamrolled by predatory lending and unnecessary add-ons, he walked away from the six-figure income to fight for the other side.
                </p>
                <p className="italic text-slate-600 border-l-4 border-emerald-600 pl-4">
                  "I know exactly what they're going to say before they say it. That's why I can help you say no."
                </p>
                <p>
                  Dave specializes in financing deep-dives, F&I survival tactics, and exposing the math behind dealer profit centers. He's the guy who reads the fine print so you don't have to.
                </p>
              </div>
            </div>

            {/* Mike */}
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-8 border border-slate-200">
              <div className="flex items-start gap-6 mb-6">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-teal-600 to-cyan-600 flex items-center justify-center text-4xl flex-shrink-0">
                  🧑‍🦲
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-slate-900 mb-2">Mike</h2>
                  <p className="text-xl text-teal-700 font-semibold mb-3">Head of Honesty</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">Truth Teller</span>
                    <span className="px-3 py-1 bg-purple-100 text-purple-800 text-xs font-semibold rounded-full">Storyteller</span>
                    <span className="px-3 py-1 bg-orange-100 text-orange-800 text-xs font-semibold rounded-full">Buyer Champion</span>
                  </div>
                </div>
              </div>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  <strong className="text-slate-900">Automotive journalist and consumer advocate.</strong> Mike has been writing about cars and the car-buying process for over 15 years, covering everything from vehicle reviews to investigative pieces on dealer fraud.
                </p>
                <p>
                  He's interviewed hundreds of buyers who've been burned, and he's made it his mission to arm consumers with the knowledge and confidence to fight back.
                </p>
                <p className="italic text-slate-600 border-l-4 border-teal-600 pl-4">
                  "The best defense against a bad deal is knowing what a good one looks like."
                </p>
                <p>
                  Mike writes the buyer guides, exposes dealer slang, and translates industry jargon into plain English. He's the voice that reminds you: you're not crazy, they really are trying to confuse you.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why We Write */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Why We Write This Blog</h2>
            <p className="text-xl text-slate-300">Our manifesto for honest car buying</p>
          </div>

          <div className="space-y-6 text-lg leading-relaxed text-slate-200">
            <p>
              <strong className="text-white">Because the car-buying process is broken.</strong> It's designed to confuse, intimidate, and extract maximum profit from people who just need reliable transportation.
            </p>
            <p>
              We've both been on the inside. We've seen the sales meetings where managers teach tactics to "overcome objections" (translation: wear you down). We've watched F&I managers add thousands in profit with a smile and a handshake. We've heard the industry jokes about "stupid customers."
            </p>
            <p className="text-2xl font-semibold text-emerald-400 text-center py-6 border-y border-slate-700">
              We're done being part of the problem. Now we're part of the solution.
            </p>
            <p>
              This blog exists to level the playing field. To give you the insider knowledge that dealerships don't want you to have. To translate the jargon, expose the tactics, and arm you with the confidence to walk away from a bad deal.
            </p>
            <p>
              <strong className="text-white">We don't sell cars. We don't take dealer money. We don't sugarcoat.</strong> We just tell you the truth — the same truth we'd tell our own family.
            </p>
            <p className="text-center text-slate-400 italic">
              Because you deserve better than the runaround.
            </p>
          </div>
        </div>
      </section>

      {/* Cross-link: Meet the Real Business */}
      <section className="py-16 bg-amber-50 border-y border-amber-100">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-shrink-0 flex items-center justify-center w-20 h-20">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-3xl">
                🏪
              </div>
            </div>
            <div className="flex-1 text-center md:text-left">
              <p className="text-xs font-bold text-amber-700 uppercase tracking-widest mb-2">Wait — These Guys Run an Actual Car Business?</p>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">
                The blog isn't just a blog. It's the philosophy behind Rockhound Auto.
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Dave and Mike aren't just writers — they're the owners of Rockhound Auto, a used car dealership and broker service on the Oregon Coast built entirely around the principles you've been reading about. No rate markup. No F&I pressure. No games. The blog is how they think. The business is how they operate.
              </p>
              <Link
                to="/about-the-owner"
                className="inline-flex items-center gap-2 bg-amber-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-amber-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-group-line"></i>
                Meet the Guys Behind the Business
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Wall */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-4 text-slate-900">
            Things We Actually Say
          </h2>
          <p className="text-center text-slate-500 mb-12 italic">
            (And have been told are "not great for dealer relationships." We're fine with that.)
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-xl border-l-4 border-emerald-600 shadow-sm">
              <p className="text-lg text-slate-700 mb-4 italic">
                "If a dealer says 'trust me,' check your wallet, re-read the contract, and run the numbers twice. Then run them again."
              </p>
              <p className="text-sm text-slate-500">— Dave, on F&I tactics</p>
            </div>
            <div className="bg-white p-8 rounded-xl border-l-4 border-teal-600 shadow-sm">
              <p className="text-lg text-slate-700 mb-4 italic">
                "The four-square is not a negotiation tool. It's a magic trick designed to confuse you. The magician is paid on commission."
              </p>
              <p className="text-sm text-slate-500">— Mike, on sales tactics</p>
            </div>
            <div className="bg-white p-8 rounded-xl border-l-4 border-emerald-600 shadow-sm">
              <p className="text-lg text-slate-700 mb-4 italic">
                "Extended warranties are profit centers dressed up as peace of mind. Do the math. Then do it again. Then ask who profits if you never use it."
              </p>
              <p className="text-sm text-slate-500">— Dave, on add-ons</p>
            </div>
            <div className="bg-white p-8 rounded-xl border-l-4 border-teal-600 shadow-sm">
              <p className="text-lg text-slate-700 mb-4 italic">
                "You're not being difficult. You're being a functioning adult with a budget. Don't let a guy on commission make you feel otherwise."
              </p>
              <p className="text-sm text-slate-500">— Mike, on buyer psychology</p>
            </div>
            <div className="bg-white p-8 rounded-xl border-l-4 border-emerald-600 shadow-sm">
              <p className="text-lg text-slate-700 mb-4 italic">
                "Nitrogen in your tires costs $299 at a dealership. It costs $0 from the atmosphere, which has been providing it for free since before cars existed."
              </p>
              <p className="text-sm text-slate-500">— Dave, on the nitrogen scam</p>
            </div>
            <div className="bg-white p-8 rounded-xl border-l-4 border-teal-600 shadow-sm">
              <p className="text-lg text-slate-700 mb-4 italic">
                "When they say 'this deal expires tonight,' what they mean is 'we'd really prefer you didn't sleep on it.' Sleep on it."
              </p>
              <p className="text-sm text-slate-500">— Mike, on manufactured urgency</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="text-5xl mb-6">🧑‍🦲🧑‍🦲</div>
          <h2 className="text-4xl font-bold mb-6">
            Ready for Some Truth?
          </h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
            Head back to the blog for no-BS car buying advice, dealer tactic breakdowns, and the insider knowledge you need to win.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/blog"
              className="inline-flex items-center justify-center gap-2 bg-white text-emerald-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-emerald-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Back to the Blog
            </Link>
            <Link
              to="/about-the-owner"
              className="inline-flex items-center justify-center gap-2 bg-emerald-800 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-emerald-900 transition-colors whitespace-nowrap"
            >
              <i className="ri-store-2-line"></i>
              See the Business They Built
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
